/**
 * Bitget WebSocket Configuration
 *
 * WebSocket channels, topology info, and low-level subscription utilities for Bitget.
 * @see https://www.bitget.com/api-doc/spot/websocket/public/Tickers-Channel
 */

import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  BitgetKlinesWsMessage,
  BitgetOrderbookWsMessage,
  BitgetTickerWsMessage,
  BitgetTradesWsMessage,
  BitgetWsStatus,
  BitgetWsSubscribeRequest,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const BITGET_WS_BASE_URL = 'wss://ws.bitget.com/v2/ws/public';

/**
 * Bitget WebSocket channel descriptors.
 * @see https://www.bitget.com/api-doc/spot/websocket/public/Tickers-Channel
 */
export const BITGET_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  orderbook: {
    channel: 'orderbook',
    topic: 'books',
    supportsBatching: true,
    docUrl: 'https://www.bitget.com/api-doc/spot/websocket/public/Depth-Channel',
  },
  ticker: {
    channel: 'ticker',
    topic: 'ticker',
    supportsBatching: true,
    docUrl: 'https://www.bitget.com/api-doc/spot/websocket/public/Tickers-Channel',
  },
  trades: {
    channel: 'trades',
    topic: 'trade',
    supportsBatching: true,
    docUrl: 'https://www.bitget.com/api-doc/spot/websocket/public/Trades-Channel',
  },
  klines: {
    channel: 'klines',
    topic: 'candle<interval>',
    supportsBatching: true,
    docUrl: 'https://www.bitget.com/api-doc/spot/websocket/public/Candlesticks-Channel',
  },
};

/**
 * Bitget WebSocket topology information.
 * Uses Bitget V2 API with JSON-based WebSocket messages.
 */
export const BITGET_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: BITGET_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 30_000,
  note: 'Supports multiplexing multiple channels on a single connection. Requires ping message every 30 seconds. Uses subscription-based model.',
};

// ========================================
// WebSocket Subscription Functions
// ========================================

/**
 * Creates a Bitget WebSocket subscription for the given topic
 */
function createBitgetWsSubscription<T>(params: {
  topic: string;
  onMessage: (data: T) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BitgetWsStatus) => void;
}): ReconnectingWebSocket {
  const { topic, onMessage, onError, onStatusChange } = params;

  const ws = new ReconnectingWebSocket(BITGET_WS_BASE_URL, [], {
    maxRetries: 10,
    minReconnectionDelay: 1000,
    maxReconnectionDelay: 30000,
    reconnectionDelayGrowFactor: 1.5,
    connectionTimeout: 10000,
  });

  let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

  const startHeartbeat = () => {
    heartbeatInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ op: 'ping' }));
      }
    }, 30_000);
  };

  const stopHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  };

  ws.addEventListener('open', () => {
    onStatusChange?.('connected');
    startHeartbeat();

    // Send subscription message
    const subscribeMessage: BitgetWsSubscribeRequest = {
      op: 'subscribe',
      args: [
        {
          instType: 'SPOT',
          channel: topic.split('@')[0], // Extract channel from topic
          instId: topic.split('@')[1], // Extract symbol from topic
        },
      ],
    };
    ws.send(JSON.stringify(subscribeMessage));
  });

  ws.addEventListener('close', () => {
    onStatusChange?.('disconnected');
    stopHeartbeat();
  });

  ws.addEventListener('error', () => {
    onStatusChange?.('error');
    onError?.(new Error('WebSocket connection error'));
  });

  ws.addEventListener('message', event => {
    try {
      const message = JSON.parse(event.data);

      // Handle pong responses
      if (message.op === 'pong') {
        return;
      }

      // Handle subscription responses
      if (message.event === 'subscribe') {
        return;
      }

      // Forward data messages to handler
      onMessage(message);
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Failed to parse message'));
    }
  });

  return ws;
}

/**
 * Subscribe to Bitget orderbook WebSocket
 */
export function subscribeBitgetOrderbook(params: {
  symbol: string;
  onMessage: (data: BitgetOrderbookWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BitgetWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createBitgetWsSubscription<BitgetOrderbookWsMessage>({
    topic: `books@${symbol}`,
    onMessage,
    onError,
    onStatusChange,
  });
}

/**
 * Subscribe to Bitget ticker WebSocket
 */
export function subscribeBitgetTicker(params: {
  symbol: string;
  onMessage: (data: BitgetTickerWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BitgetWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createBitgetWsSubscription<BitgetTickerWsMessage>({
    topic: `ticker@${symbol}`,
    onMessage,
    onError,
    onStatusChange,
  });
}

/**
 * Subscribe to Bitget trades WebSocket
 */
export function subscribeBitgetTrades(params: {
  symbol: string;
  onMessage: (data: BitgetTradesWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BitgetWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createBitgetWsSubscription<BitgetTradesWsMessage>({
    topic: `trade@${symbol}`,
    onMessage,
    onError,
    onStatusChange,
  });
}

/**
 * Subscribe to Bitget klines WebSocket
 */
export function subscribeBitgetKlines(params: {
  symbol: string;
  interval: string;
  onMessage: (data: BitgetKlinesWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BitgetWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, interval, onMessage, onError, onStatusChange } = params;
  return createBitgetWsSubscription<BitgetKlinesWsMessage>({
    topic: `candle${interval}@${symbol}`,
    onMessage,
    onError,
    onStatusChange,
  });
}
